import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const LOGO_URL = "/brand/logo.png";

const foundationSubmenu = [
    { path: "/", label: "Accueil" },
    { path: "/a-propos", label: "À propos" },
    { path: "/histoire", label: "Mon histoire" },
    { path: "/logo", label: "Notre logo" },
];

// "Soins & Guidance" stays a clickable link, but its dropdown surfaces the
// "Offrir un soin" CTA that used to be a top-level menu item.
// Rationale: page /offrir is low-traffic direct but discoverable naturally
// when someone considers gifting a session.
const soinsSubmenu = [
    { path: "/offrir", label: "Offrir un soin" },
];

// Grouped content pages so the nav stays on a single line.
const ressourcesSubmenu = [
    { path: "/journal", label: "Journal" },
    { path: "/transmissions", label: "Vidéos" },
];

const nav = [
    { to: "/", label: "Découvrir", submenu: foundationSubmenu },
    { to: "/langage-de-lumiere", label: "Langage de lumière" },
    { to: "/evenements", label: "Ateliers" },
    { to: "/abonnements", label: "Abonnements" },
    { to: "/reservation", label: "Soins & Guidance", submenu: soinsSubmenu },
    { to: "/accompagnements", label: "Accompagnements" },
    { to: "/journal", label: "Ressources", submenu: ressourcesSubmenu },
];

export default function Header() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const isAdmin = user && user !== false && user.role === "admin";

    return (
        <header
            className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40"
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-20">
                <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
                    <img
                        src={LOGO_URL}
                        alt="Foundation of Light"
                        width="40"
                        height="40"
                        loading="eager"
                        decoding="async"
                        className="w-10 h-10 object-contain shrink-0"
                    />
                    <span className="font-serif text-xl leading-none tracking-wide hidden sm:block">
                        Foundation<br /><span className="text-sm tracking-[0.2em] text-muted-foreground">of Light</span>
                    </span>
                </Link>
                <nav className="hidden lg:flex items-center gap-6 ml-8 xl:ml-12">
                    {nav.map((n) => {
                        const active = location.pathname === n.to;
                        if (n.submenu) {
                            return (
                                <div key={n.to} className="relative group" data-testid={`nav-${n.to.replace("/", "") || "home"}-wrapper`}>
                                    <Link
                                        to={n.to}
                                        data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                                        className={`text-[13px] whitespace-nowrap inline-flex items-center gap-1 transition-colors ${
                                            active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                                        }`}
                                    >
                                        {n.label}
                                        <ChevronDown className="w-3.5 h-3.5 opacity-70 group-hover:rotate-180 transition-transform" />
                                    </Link>
                                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity">
                                        <div className="rounded-2xl border border-border/60 bg-background/95 backdrop-blur-xl shadow-xl py-2 min-w-[200px]">
                                            {n.submenu.map((s) => (
                                                <Link
                                                    key={s.hash || s.path}
                                                    to={s.path ? s.path : `${n.to}#${s.hash}`}
                                                    className="block px-5 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors whitespace-nowrap"
                                                    data-testid={`nav-sub-${s.hash || s.path.replace("/", "")}`}
                                                >
                                                    {s.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                                className={`text-[13px] whitespace-nowrap transition-colors ${
                                    active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                {n.label}
                            </Link>
                        );
                    })}
                    {isAdmin && (
                        <Link to="/communaute" className="text-[13px] whitespace-nowrap text-primary hover:underline" data-testid="nav-communaute">
                            Communauté
                        </Link>
                    )}
                    {isAdmin && (
                        <Link to="/admin" className="text-[13px] whitespace-nowrap text-secondary hover:underline" data-testid="nav-admin">
                            Admin
                        </Link>
                    )}
                </nav>
                <div className="hidden md:flex items-center gap-3">
                    {user && user !== false ? (
                        <>
                            <Button variant="ghost" className="rounded-full" onClick={() => navigate("/espace")} data-testid="header-dashboard-btn">
                                {user.name}
                            </Button>
                            <Button variant="outline" className="rounded-full" onClick={async () => { await logout(); navigate("/"); }} data-testid="header-logout-btn">
                                Se déconnecter
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" className="rounded-full" onClick={() => navigate("/connexion")} data-testid="header-login-btn">
                                Connexion
                            </Button>
                            <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/inscription")} data-testid="header-signup-btn">
                                Rejoindre
                            </Button>
                        </>
                    )}
                </div>
                <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
                    {open ? <X /> : <Menu />}
                </button>
            </div>
            {open && (
                <div className="lg:hidden border-t border-border/40 bg-background/95 backdrop-blur-xl">
                    <div className="flex flex-col p-6 gap-4">
                        {nav.map((n) => (
                            <div key={n.to} className="flex flex-col gap-2">
                                <Link to={n.to} onClick={() => setOpen(false)} className="text-lg" data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}>
                                    {n.label}
                                </Link>
                                {n.submenu && (
                                    <div className="pl-4 flex flex-col gap-2 border-l border-border/40">
                                        {n.submenu.map((s) => (
                                            <Link
                                                key={s.hash || s.path}
                                                to={s.path ? s.path : `${n.to}#${s.hash}`}
                                                onClick={() => setOpen(false)}
                                                className="text-sm text-muted-foreground"
                                                data-testid={`mobile-nav-sub-${s.hash || s.path.replace("/", "")}`}
                                            >
                                                {s.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {isAdmin && <Link to="/communaute" onClick={() => setOpen(false)} className="text-lg text-primary">Communauté</Link>}
                        {isAdmin && <Link to="/admin" onClick={() => setOpen(false)} className="text-lg text-secondary">Admin</Link>}
                        {user && user !== false ? (
                            <>
                                <Button className="rounded-full" onClick={() => { setOpen(false); navigate("/espace"); }}>Mon espace</Button>
                                <Button variant="outline" className="rounded-full" onClick={async () => { await logout(); setOpen(false); navigate("/"); }}>Se déconnecter</Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outline" className="rounded-full" onClick={() => { setOpen(false); navigate("/connexion"); }}>Connexion</Button>
                                <Button className="rounded-full" onClick={() => { setOpen(false); navigate("/inscription"); }}>Rejoindre</Button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
